import {
  ChakraFlex,
  FAIconName,
  Icon,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';
import CollapsibleUserCard from '../../common/CollapsibleUserCard';
import { AvatarUserTypeDisplay } from '~/lib/constants/patients.constants';
import { useMemo } from 'react';
import { getDOBString, getItemsFromList, getLocation } from './utils';
import {
  getPreferredFullNameV2,
  shouldHideLanguage,
  shouldHideTreatmentProgram,
} from '~/lib/util/patient.util';

const TOP_LEVEL_ID = 'patient-info-card';

export enum PatientAttributeLabel {
  DOB = 'DOB',
  LOCATION = 'Location',
  LANGUAGE = 'Language',
  DIAGNOSIS = 'Diagnosis',
  TREATMENT_PROGRAM = 'Treatment program',
  ADMIT_DATE = 'Admit date',
}

interface PatientInfoCardProps {
  admitDate: string;
  diagnosis: string;
  isLoading: boolean;
  patient: Patient;
  treatmentProgram: string;
}

const PatientInfoCard = ({
  admitDate,
  diagnosis,
  isLoading,
  patient,
  treatmentProgram,
}: PatientInfoCardProps) => {
  const attributes = useMemo(() => {
    if (!patient) return [];

    return [
      {
        icon: FAIconName.calendarRegular,
        label: PatientAttributeLabel.DOB,
        value: getDOBString(patient.dateOfBirth),
      },
      {
        icon: FAIconName.mapMarkerAltSolid,
        label: PatientAttributeLabel.LOCATION,
        value: getLocation(patient.state, patient.timezone),
      },
      {
        icon: FAIconName.commentRegular,
        label: PatientAttributeLabel.LANGUAGE,
        value: shouldHideLanguage(patient.languages)
          ? null
          : getItemsFromList(patient.languages),
      },
      {
        icon: FAIconName.filePlusRegular,
        label: PatientAttributeLabel.DIAGNOSIS,
        value: diagnosis,
      },
      {
        icon: FAIconName.folderMedical,
        label: PatientAttributeLabel.TREATMENT_PROGRAM,
        value: shouldHideTreatmentProgram(treatmentProgram)
          ? null
          : treatmentProgram,
      },
      {
        icon: FAIconName.bullseye,
        label: PatientAttributeLabel.ADMIT_DATE,
        value: admitDate,
      },
    ].filter(({ value }) => value != null);
  }, [admitDate, diagnosis, patient, treatmentProgram]);

  return (
    <SkeletonLoader id={TOP_LEVEL_ID} isLoaded={!isLoading} width="100%">
      <CollapsibleUserCard
        avatarProps={{
          name: getPreferredFullNameV2(patient),
          src: patient?.avatarSrc,
          userType: 'patient',
        }}
        data-testid="patient-info-card"
        expandIconColor="primary.100"
        id={TOP_LEVEL_ID}
        nameProps={{
          chosenName: patient?.chosenName,
          firstName: patient?.firstName,
          lastName: patient?.lastName,
        }}
        pronouns={patient?.pronounsOnRecord}
        role={AvatarUserTypeDisplay.patient}
      >
        <ChakraFlex direction="column" gap="6px" marginTop="12px">
          {attributes.map(({ icon, label, value }) => (
            <ChakraFlex
              alignItems="center"
              gap="4px"
              id={`${TOP_LEVEL_ID}__${label}`}
              key={label}
            >
              <Icon
                aria-hidden
                color="neutral.secondary"
                name={icon}
                size="md"
              />
              <Text color="neutral.secondary" variant="caption">
                {value}
              </Text>
            </ChakraFlex>
          ))}
        </ChakraFlex>
      </CollapsibleUserCard>
    </SkeletonLoader>
  );
};

export default PatientInfoCard;
